/**
 * The suite shelf — a synthetic folder every hosted pane can see, without a
 * picker and without a permission ask.
 *
 * Ordinary pane-fs folders are real directories the shell's user granted; the
 * shelf is not one of them. It lives in the host's own IndexedDB and holds
 * staged file payloads, so an application that cannot show a picker (sak's
 * vault files are server-backed, not disk files) can hand a document to the
 * editors: sak writes bytes onto the shelf (`fs:call writeBinary` with
 * `folder: SUITE_SHELF_FOLDER`), kor/yaz/kod open and save them back through
 * their HostBridgeAdapter bound to the same folder name. The vocabulary is the
 * existing `PaneFsOp` set — no new bridge messages, no new permissions.
 */

import type { PaneFsOp } from './protocol';

/** Synthetic folder name addressing the shelf in every fs call. */
export const SUITE_SHELF_FOLDER = '__shelf__';

const DB_NAME = 'suite-shelf';
const STORE = 'files';

let dbPromise: Promise<IDBDatabase | null> | null =
  typeof indexedDB !== "undefined"
    ? new Promise((resolve) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      })
    : null;

interface ShelfEntry {
  /** Raw bytes; text writes are UTF-8 encoded into the same shape. */
  bytes: Uint8Array;
  mtime: number;
  /** True for explicit directory markers (mkdir). */
  isDirectory: boolean;
}

function shelfGet(key: string): Promise<ShelfEntry | undefined> {
  return (async () => {
    const db = await dbPromise;
    if (!db) return undefined;
    return new Promise<ShelfEntry | undefined>((resolve) => {
      const req = db.transaction(STORE).objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result as ShelfEntry | undefined);
      req.onerror = () => resolve(undefined);
    });
  })();
}

function shelfSet(key: string, value: ShelfEntry): Promise<void> {
  return (async () => {
    const db = await dbPromise;
    if (!db) throw new Error('The suite shelf store is unavailable in this environment');
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  })();
}

function shelfDelete(key: string): Promise<void> {
  return (async () => {
    const db = await dbPromise;
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  })();
}

function shelfKeys(): Promise<string[]> {
  return (async () => {
    const db = await dbPromise;
    if (!db) return [];
    return new Promise<string[]>((resolve) => {
      const req = db.transaction(STORE).objectStore(STORE).getAllKeys();
      req.onsuccess = () => resolve((req.result as string[]) ?? []);
      req.onerror = () => resolve([]);
    });
  })();
}

/** Normalize a shelf path: leading slash, no trailing slash, no empty parts. */
function normalizePath(path: string): string {
  const parts = String(path ?? '').split('/').filter((part) => part && part !== '.');
  return `/${parts.join('/')}`;
}

const encodeText = (text: string): Uint8Array => new TextEncoder().encode(text);
const decodeBytes = (bytes: Uint8Array): string => new TextDecoder().decode(bytes);

/** UTF-8 text of a stored file, or null when the key holds nothing. */
async function readFileBytes(path: string): Promise<Uint8Array | null> {
  const entry = await shelfGet(normalizePath(path));
  if (!entry || entry.isDirectory) return null;
  return entry.bytes;
}

interface StatEntry {
  name: string;
  path: string;
  isFile: boolean;
  isDirectory: boolean;
  mtime: number;
  ctime: number;
  size: number;
}

async function listChildren(dirPath: string, detailed: boolean): Promise<string[] | StatEntry[]> {
  const dir = normalizePath(dirPath);
  const prefix = dir === '/' ? '/' : `${dir}/`;
  const keys = await shelfKeys();
  const names = new Map<string, StatEntry>();
  for (const key of keys) {
    if (!key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    if (!rest) continue;
    const slash = rest.indexOf('/');
    const name = slash === -1 ? rest : rest.slice(0, slash);
    if (names.has(name)) continue;
    if (slash === -1) {
      const entry = await shelfGet(key);
      if (!entry) continue;
      names.set(name, {
        name,
        path: `${prefix}${name}`.replace(/^\/(\/)/, '$1'),
        isFile: !entry.isDirectory,
        isDirectory: entry.isDirectory,
        mtime: entry.mtime,
        ctime: entry.mtime,
        size: entry.bytes.byteLength,
      });
    } else {
      names.set(name, {
        name,
        path: `${prefix}${name}`.replace(/^\/(\/)/, '$1'),
        isFile: false,
        isDirectory: true,
        mtime: 0,
        ctime: 0,
        size: 0,
      });
    }
  }
  const entries = Array.from(names.values());
  return detailed ? entries : entries.map((entry) => entry.name);
}

/**
 * Run one `PaneFsOp` against the shelf. Semantics mirror the FSA host's own
 * operations so a pane cannot tell the difference: data-URL images on `write`,
 * implicit directories, recursive `rm`, copy-overwrite `rename`.
 */
export async function runShelfOp(op: PaneFsOp, args: unknown[]): Promise<unknown> {
  const path = normalizePath(typeof args[0] === 'string' ? (args[0] as string) : '/');

  switch (op) {
    case 'dirname':
      return SUITE_SHELF_FOLDER;

    case 'list':
      return await listChildren(path, false);

    case 'listDetailed':
      return await listChildren(path, true);

    case 'read': {
      const bytes = await readFileBytes(path);
      if (bytes === null) throw new Error(`No shelf file at "${path}"`);
      return decodeBytes(bytes);
    }

    case 'readBinary': {
      const bytes = await readFileBytes(path);
      if (bytes === null) throw new Error(`No shelf file at "${path}"`);
      return bytes;
    }

    case 'write': {
      const content = String(args[1] ?? '');
      // Image data URLs ride as bytes, exactly like the FSA host branch does.
      if (/\.(png|jpg|jpeg|gif|webp|svg|bmp|tiff|ico)$/i.test(path) && content.startsWith('data:image/')) {
        const comma = content.indexOf(',');
        const payload = comma >= 0 ? content.slice(comma + 1) : '';
        const raw = atob(payload);
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
        await shelfSet(path, { bytes, mtime: Date.now(), isDirectory: false });
      } else {
        await shelfSet(path, { bytes: encodeText(content), mtime: Date.now(), isDirectory: false });
      }
      return undefined;
    }

    case 'writeBinary': {
      const data = args[1] as Uint8Array | ArrayBuffer | null;
      if (!data) throw new Error('writeBinary requires data');
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
      await shelfSet(path, { bytes, mtime: Date.now(), isDirectory: false });
      return undefined;
    }

    case 'exists': {
      if (path === '/') return true;
      const entry = await shelfGet(path);
      if (entry) return true;
      // A directory exists when any key hangs beneath it.
      const prefix = `${path}/`;
      return (await shelfKeys()).some((key) => key.startsWith(prefix));
    }

    case 'mkdir': {
      if (path === '/') return undefined;
      await shelfSet(path, { bytes: new Uint8Array(), mtime: Date.now(), isDirectory: true });
      return undefined;
    }

    case 'rm': {
      if (path === '/') throw new Error('Cannot delete the shelf root');
      const entry = await shelfGet(path);
      if (entry) {
        await shelfDelete(path);
        return undefined;
      }
      // Recursive delete of an implicit directory.
      const prefix = `${path}/`;
      for (const key of await shelfKeys()) {
        if (key.startsWith(prefix)) await shelfDelete(key);
      }
      return undefined;
    }

    case 'stat': {
      const entry = await shelfGet(path);
      if (entry) {
        return {
          isFile: !entry.isDirectory,
          isDirectory: entry.isDirectory,
          mtime: entry.mtime,
          size: entry.bytes.byteLength,
          ctime: entry.mtime,
        };
      }
      const prefix = `${path}/`;
      if (path !== '/' && (await shelfKeys()).some((key) => key.startsWith(prefix))) {
        return { isFile: false, isDirectory: true, mtime: 0, size: 0, ctime: 0 };
      }
      throw new Error(`No shelf entry at "${path}"`);
    }

    case 'rename': {
      const newPath = normalizePath(String(args[1] ?? ''));
      if (!newPath || newPath === '/') throw new Error('rename requires a destination path');
      const bytes = await readFileBytes(path);
      if (bytes !== null) {
        await shelfSet(newPath, { bytes, mtime: Date.now(), isDirectory: false });
        await shelfDelete(path);
        return undefined;
      }
      // Directory rename: move every child key.
      const prefix = `${path}/`;
      let moved = false;
      for (const key of await shelfKeys()) {
        if (!key.startsWith(prefix)) continue;
        const entry = await shelfGet(key);
        if (!entry) continue;
        await shelfSet(`${newPath}/${key.slice(prefix.length)}`, entry);
        await shelfDelete(key);
        moved = true;
      }
      if (!moved) throw new Error(`No shelf entry at "${path}" to rename`);
      return undefined;
    }

    default:
      throw new Error(`Unknown shelf operation: ${op}`);
  }
}